import { useMutation, useQuery } from '@tanstack/react-query';
import { withAppHOC } from '../../hoc';

import {
  StyledContentContainer,
  StyledIconedText,
} from '../../components/styled';

import PageHeader from '../../components/PageHeader/PageHeader';
import { MessageBox, Switch } from '../../components/FormElements';

import Loader from '../../components/Loader/Loader';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import NoData from '../../components/NoData/NoData';

import { TableBuilder } from '../../components/TableElements';

import API from '../../api';
import { RESPONSE_STATUSES } from '../../constants';
import { formatCurrency } from '../../utils';

import { Image } from '../../components/Image';

const LiveMarginsList = () => {
  const readData = async () => {
    try {
      const response = await API.get('/buyer/live-margins');
      return response?.data;
    } catch (error) {
      throw new Error(error?.message || 'Failed to load live margins');
    }
  };

  const {
    data,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['Live_Margins'],
    queryFn: readData,
  });

  const reloadData = async () => {
    await refetch();
  };

  /*
   * Keep your existing updateData function here.
   * If you already have it imported/defined elsewhere,
   * use that instead.
   */
  const updateData = async ({ value, newData }) => {
    // Your existing update API logic
  };

  const { mutateAsync: updateDataMutation } = useMutation({
    mutationFn: updateData,

    onError: (error) => {
      console.error(error);
    },

    onSuccess: () => {
      reloadData();
    },
  });

  const renderPageHeading = () => (
    <PageHeader
      title="Live Operator Margins"
      showAddButton={false}
      showReloadData
      reloadData={reloadData}
    />
  );

  if (isLoading) {
    return (
      <>
        {renderPageHeading()}

        <StyledContentContainer>
          <LoadingSpinner />
        </StyledContentContainer>
      </>
    );
  }

  if (error) {
    return (
      <>
        {renderPageHeading()}

        <StyledContentContainer>
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={error.message}
          />
        </StyledContentContainer>
      </>
    );
  }

  return (
    <>
      <Loader isLoading={isRefetching} />

      {renderPageHeading()}

      <StyledContentContainer>
        {data?.status === RESPONSE_STATUSES.FAILED && (
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={data?.message}
          />
        )}

        {!data?.data || data.data.length === 0 ? (
          <NoData />
        ) : (
          <TableBuilder
            isLoading={isLoading}
            tableHeadings={[
              {
                title: 'Operator',
                dataSelector: 'operatorName',
                dataType: 'string',

                cellrenderer: (value, item) => (
                  <StyledIconedText>
                    <Image
                      source={
                        item?.operatorLogo || '/no-image.png'
                      }
                      alt={value}
                      style={{
                        width: '40px',
                        height: '40px',
                      }}
                    />

                    {value}
                  </StyledIconedText>
                ),
              },

              {
                title: 'Margin',
                dataSelector: 'margin',
                dataType: 'string',

                cellrenderer: (value) =>
                  value !== null &&
                  value !== undefined &&
                  value !== ''
                    ? `${value}%`
                    : 'N/A',
              },
            ]}

            tableData={data?.data || []}
          />
        )}
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(LiveMarginsList);